import { ApiError } from './errors';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `details.backend.fields` as a string map; empty when absent or not ApiError.
 * The values are the server's English text. Callers may use the keys only —
 * `useActionError` and the rate-card and plan mappers. No component renders the values.
 */
export function fieldErrorsOf(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  const backend = err.details?.backend;
  if (!isPlainObject(backend)) return {};
  const fields = backend['fields'];
  if (!isPlainObject(fields)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (typeof value === 'string' && value.length > 0) out[key] = value;
  }
  return out;
}

/** `phases[1].rateBp` → `phases`, `a.b` → `a`. A key with neither stays whole. */
export function fieldBaseName(key: string): string {
  const bracket = key.indexOf('[');
  const dot = key.indexOf('.');
  let end = key.length;
  if (bracket >= 0 && bracket < end) end = bracket;
  if (dot >= 0 && dot < end) end = dot;
  return key.slice(0, end);
}

/**
 * Server field keys → the form's fields. A key matches the mapping directly, or by its base
 * name (`phases[1].rateBp` → `phases`). Unknown keys are dropped. The result marks fields;
 * it carries no server text.
 */
export function mapFieldErrors<F extends string>(
  fieldKeys: readonly string[],
  mapping: Readonly<Record<string, F>>,
): Partial<Record<F, true>> {
  const out: Partial<Record<F, true>> = {};
  for (const key of fieldKeys) {
    const mapped = mapping[key] ?? mapping[fieldBaseName(key)];
    if (mapped !== undefined) out[mapped] = true;
  }
  return out;
}

/** The first marked field in `order`, for focus after a failed submit (FRM-005). */
export function firstMarkedField<F extends string>(
  order: readonly F[],
  marked: Partial<Record<F, true>>,
): F | undefined {
  return order.find((field) => marked[field] === true);
}
